/**
 * RoomOps LIVE monitor: badge shows confirmation number or guest as initial + last name (from /getroomstatuses).
 * Preference is stored per user + property in localStorage.
 */

import { reservationRowForDates, isTruthyMonitorTodayFlag } from './monitorReservationUtils';

export const BADGE_DISPLAY_CONFIRMATION = 'confirmation';
export const BADGE_DISPLAY_GUEST = 'guest_name';

/** Max visible characters for confirmation badges on narrow room cards */
export const MONITOR_BADGE_MAX_CHARS = 10;

/** Guest line e.g. "J. Smith" — allow slightly longer strings on narrow badges */
export const MONITOR_GUEST_BADGE_MAX_CHARS = 14;

export function getMonitorBadgeDisplayStorageKey(login) {
    if (!login || typeof login !== 'object') {
        return 'roomOpsMonitor_badgeDisplay_default';
    }
    const pid = String(login.properties_id ?? login.propertiesId ?? '0');
    const user = String(
        login.email ||
            login.users_id ||
            login.user_id ||
            login.username ||
            'default'
    );
    return `roomOpsMonitor_badgeDisplay_${pid}_${user}`;
}

export function readStoredBadgeDisplay(login) {
    try {
        const key = getMonitorBadgeDisplayStorageKey(login);
        const v = localStorage.getItem(key);
        return v === BADGE_DISPLAY_GUEST ? BADGE_DISPLAY_GUEST : BADGE_DISPLAY_CONFIRMATION;
    } catch {
        return BADGE_DISPLAY_CONFIRMATION;
    }
}

export function writeStoredBadgeDisplay(login, mode) {
    try {
        const key = getMonitorBadgeDisplayStorageKey(login);
        const value =
            mode === BADGE_DISPLAY_GUEST ? BADGE_DISPLAY_GUEST : BADGE_DISPLAY_CONFIRMATION;
        localStorage.setItem(key, value);
    } catch {
        /* ignore */
    }
}

export function getMonitorCompactCellsStorageKey(login) {
    if (!login || typeof login !== 'object') {
        return 'roomOpsMonitor_compactCells_default';
    }
    const pid = String(login.properties_id ?? login.propertiesId ?? '0');
    const user = String(
        login.email ||
            login.users_id ||
            login.user_id ||
            login.username ||
            'default'
    );
    return `roomOpsMonitor_compactCells_${pid}_${user}`;
}

export function readStoredCompactRoomCells(login) {
    try {
        const key = getMonitorCompactCellsStorageKey(login);
        return localStorage.getItem(key) === '1';
    } catch {
        return false;
    }
}

export function writeStoredCompactRoomCells(login, compact) {
    try {
        localStorage.setItem(getMonitorCompactCellsStorageKey(login), compact ? '1' : '0');
    } catch {
        /* ignore */
    }
}

export function getMonitorUnassignedReservationsStorageKey(login) {
    if (!login || typeof login !== 'object') {
        return 'roomOpsMonitor_showUnassignedReservations_default';
    }
    const pid = String(login.properties_id ?? login.propertiesId ?? '0');
    const user = String(
        login.email ||
            login.users_id ||
            login.user_id ||
            login.username ||
            'default'
    );
    return `roomOpsMonitor_showUnassignedReservations_${pid}_${user}`;
}

export function readStoredShowUnassignedReservations(login) {
    try {
        const key = getMonitorUnassignedReservationsStorageKey(login);
        const value = localStorage.getItem(key);
        return value !== '0';
    } catch {
        return true;
    }
}

export function writeStoredShowUnassignedReservations(login, show) {
    try {
        localStorage.setItem(getMonitorUnassignedReservationsStorageKey(login), show ? '1' : '0');
    } catch {
        /* ignore */
    }
}

/**
 * @param {object|string} conf - { confirmation_number, guest_first?, guest_last?, ... } or raw confirmation string
 * @param {string} mode - BADGE_DISPLAY_CONFIRMATION | BADGE_DISPLAY_GUEST
 */
/** Bootstrap success / danger — monitor door badges */
export const MONITOR_BADGE_CHECKIN_COLOR = '#28a745';
export const MONITOR_BADGE_CHECKOUT_COLOR = '#dc3545';

/** Same gradient on door cards, compact dots, and reservation-column list badges */
export const MONITOR_BADGE_DAY_USE_GRADIENT_BG = `linear-gradient(to bottom, ${MONITOR_BADGE_CHECKIN_COLOR} 0%, ${MONITOR_BADGE_CHECKIN_COLOR} 50%, ${MONITOR_BADGE_CHECKOUT_COLOR} 50%, ${MONITOR_BADGE_CHECKOUT_COLOR} 100%)`;

/**
 * Monitor door card inner badge row (non–process mode): {@link MontiorCard} uses `15 * 0.75`.
 * List/column badges should match so split badges are not taller than door labels.
 */
export const MONITOR_DOOR_BADGE_ROW_MIN_HEIGHT_PX = Math.round(15 * 0.75);
export const MONITOR_DOOR_BADGE_ROW_FONT_SIZE_PX = 12;
export const MONITOR_DOOR_BADGE_ROW_PADDING = '1px 8px';

/** Standalone list/column badges (not door-hanging tags). */
export const MONITOR_STANDALONE_BADGE_BORDER_RADIUS = '4px';

/** Room type code on unassigned two-tone badges (maroon). */
export const MONITOR_UNASSIGNED_BADGE_ROOM_TYPE_COLOR = '#e1346e';

/** Layout space above compact room circles so reservation dots are not clipped. */
export const COMPACT_MONITOR_DOT_PADDING_TOP_PX = 10;

/** Inline styles for a single-shell standalone badge (background + shadow on one node). */
export function getMonitorStandaloneBadgeShellStyle(pres) {
    return {
        color: '#fff',
        fontWeight: 'bold',
        borderRadius: MONITOR_STANDALONE_BADGE_BORDER_RADIUS,
        maxWidth: 120,
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
        marginBottom: 2,
        cursor: 'pointer',
        transition: 'opacity 0.2s',
        minHeight: MONITOR_DOOR_BADGE_ROW_MIN_HEIGHT_PX,
        padding: MONITOR_DOOR_BADGE_ROW_PADDING,
        fontSize: MONITOR_DOOR_BADGE_ROW_FONT_SIZE_PX,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...(pres.kind === 'split'
            ? { background: MONITOR_BADGE_DAY_USE_GRADIENT_BG }
            : { backgroundColor: pres.backgroundColor })
    };
}

/** Outer shell for unassigned two-tone badges (green confirmation header + white room type). */
export function getMonitorUnassignedBadgeOuterStyle() {
    return {
        borderRadius: MONITOR_STANDALONE_BADGE_BORDER_RADIUS,
        overflow: 'hidden',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
        maxWidth: 120,
        minWidth: 72,
        marginBottom: 2,
        cursor: 'pointer',
        transition: 'opacity 0.2s',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch'
    };
}

/** Max characters for the single centered label on a merged check-in + check-out door badge */
const MERGED_DAY_USE_CENTER_DISPLAY_MAX = 22;

function truncateMergedDayUseCenterDisplay(text) {
    if (text.length <= MERGED_DAY_USE_CENTER_DISPLAY_MAX) {
        return text;
    }
    return `${text.slice(0, MERGED_DAY_USE_CENTER_DISPLAY_MAX - 1)}…`;
}

/**
 * When a single reservation row has both check-in and check-out today (day use), merge into one
 * vertical split badge. Turnover rooms (separate check-in-only and checkout-only rows) return
 * null so the door card renders two stacked badges (check-in on top, check-out on bottom).
 *
 * @param {Array<object>} rows - Same shape as `confirmation_numbers` entries (sorted like monitor badges).
 * @param {string} badgeDisplayMode
 * @returns {null | object} `gradient_name` when one row has both today flags; otherwise null.
 */
function normalizeMonitorBadgeRowForDayUse(r) {
    if (!r || typeof r !== 'object') {
        return r;
    }
    const row = reservationRowForDates(r);
    const checkin_today =
        isTruthyMonitorTodayFlag(row.checkin_today) || isTruthyMonitorTodayFlag(row.check_in_today);
    const checkout_today =
        isTruthyMonitorTodayFlag(row.checkout_today) || isTruthyMonitorTodayFlag(row.check_out_today);
    return { ...row, checkin_today, checkout_today };
}

export function getMonitorDayUseSplitBadgePlan(rows, badgeDisplayMode) {
    if (!rows || rows.length === 0) {
        return null;
    }

    const normRows = rows.map((row) => normalizeMonitorBadgeRowForDayUse(row)).filter(Boolean);

    if (normRows.length === 1) {
        const r = normRows[0];
        if (r.checkin_today && r.checkout_today) {
            const { display, tooltip, confirmationForClick } = getMonitorBadgeLabel(r, badgeDisplayMode);
            return {
                kind: 'gradient_name',
                display,
                tooltip,
                confirmationForClick
            };
        }
        return null;
    }

    // Turnover (one departing + one arriving reservation): show separate badges, not a merged gradient.
    return null;
}

/** Same-day changeover with distinct arriving and departing rows (not day-use on one row). */
export function hasMonitorCrossReservationTurnover(roomOrRows) {
    const segments = Array.isArray(roomOrRows?.confirmation_numbers)
        ? roomOrRows.confirmation_numbers.filter((c) => c && typeof c === 'object')
        : Array.isArray(roomOrRows)
          ? roomOrRows.filter((c) => c && typeof c === 'object')
          : [];
    if (segments.length < 2) {
        return false;
    }
    const normalized = segments.map((c) => normalizeMonitorBadgeRowForDayUse(c));
    if (
        normalized.length === 1 &&
        normalized[0].checkin_today &&
        normalized[0].checkout_today
    ) {
        return false;
    }
    const checkinOnly = normalized.some((c) => c.checkin_today && !c.checkout_today);
    const checkoutOnly = normalized.some((c) => c.checkout_today && !c.checkin_today);
    return checkinOnly && checkoutOnly;
}

/**
 * Visual plan for standalone list badges (Unassigned Reservations column, reservations overview, multi-property column).
 * Matches door-card split styling when check-in and check-out both apply; uses solid colors otherwise.
 * Unassigned reservations use a two-tone card: confirmation/guest header + room_type_code body.
 *
 * @param {object|string} conf
 * @param {string} badgeDisplayMode
 * @param {{ isUnassigned?: boolean }} options
 * @returns {{ kind: 'split', display: string, tooltip: string, confirmationForClick: string } | { kind: 'unassigned', display: string, roomTypeCode: string, tooltip: string, confirmationForClick: string, backgroundColor: string } | { kind: 'solid', display: string, tooltip: string, confirmationForClick: string, backgroundColor: string }}
 */
export function getMonitorStandaloneBadgePresentation(conf, badgeDisplayMode, options = {}) {
    const { isUnassigned = false } = options;
    const obj =
        typeof conf === 'object' && conf !== null
            ? conf
            : { confirmation_number: conf != null && conf !== '' ? String(conf) : '' };

    if (isUnassigned) {
        const row = reservationRowForDates(obj) || obj;
        const { display, confirmationForClick } = getMonitorBadgeLabel(row, badgeDisplayMode);
        const confirmationRaw =
            row.confirmation_number != null && String(row.confirmation_number).trim() !== ''
                ? String(row.confirmation_number).trim()
                : '';
        const roomTypeCode =
            row.room_type_code != null && String(row.room_type_code).trim() !== ''
                ? String(row.room_type_code).trim()
                : '—';
        const guestFirstRaw =
            row.guest_first != null && String(row.guest_first).trim() !== ''
                ? String(row.guest_first).trim()
                : '';
        const guestLast =
            row.guest_last != null && String(row.guest_last).trim() !== ''
                ? String(row.guest_last).trim()
                : '';
        const fullGuestTooltip =
            guestFirstRaw && guestLast
                ? `${guestFirstRaw} ${guestLast}`
                : guestLast || guestFirstRaw || '';
        const extras = [
            badgeDisplayMode === BADGE_DISPLAY_GUEST && fullGuestTooltip ? fullGuestTooltip : '',
            roomTypeCode !== '—' ? roomTypeCode : ''
        ].filter(Boolean);
        return {
            kind: 'unassigned',
            display,
            roomTypeCode,
            tooltip: `Click to assign reservation${confirmationRaw ? ` ${confirmationRaw}` : ''}${
                extras.length ? ` (${extras.join(', ')})` : ''
            }`,
            confirmationForClick: confirmationForClick || confirmationRaw,
            backgroundColor: MONITOR_BADGE_CHECKIN_COLOR
        };
    }

    const plan = getMonitorDayUseSplitBadgePlan([obj], badgeDisplayMode);
    if (plan && plan.kind === 'gradient_name') {
        const confirmationForClick = plan.confirmationForClick;
        return {
            kind: 'split',
            display: plan.display,
            tooltip: plan.tooltip,
            confirmationForClick
        };
    }

    const { display, tooltip, confirmationForClick } = getMonitorBadgeLabel(obj, badgeDisplayMode);
    const n = normalizeMonitorBadgeRowForDayUse(obj);
    let backgroundColor = '#06A9F4';
    if (n.checkout_today) {
        backgroundColor = MONITOR_BADGE_CHECKOUT_COLOR;
    } else if (n.checkin_today) {
        backgroundColor = MONITOR_BADGE_CHECKIN_COLOR;
    }

    return {
        kind: 'solid',
        display,
        tooltip,
        confirmationForClick,
        backgroundColor
    };
}

export function getMonitorBadgeLabel(conf, mode) {
    const obj = typeof conf === 'object' && conf !== null ? conf : {};
    const confirmationNumber =
        obj.confirmation_number != null && obj.confirmation_number !== ''
            ? String(obj.confirmation_number)
            : typeof conf === 'string'
              ? conf
              : '';
    const guestFirstRaw =
        obj.guest_first != null && String(obj.guest_first).trim() !== ''
            ? String(obj.guest_first).trim()
            : '';
    const guestLast =
        obj.guest_last != null && String(obj.guest_last).trim() !== ''
            ? String(obj.guest_last).trim()
            : '';

    const guestDisplayLine =
        guestFirstRaw && guestLast
            ? `${guestFirstRaw.charAt(0).toUpperCase()}. ${guestLast}`
            : guestLast
              ? guestLast
              : guestFirstRaw
                ? guestFirstRaw.length > MONITOR_GUEST_BADGE_MAX_CHARS
                    ? `${guestFirstRaw.slice(0, MONITOR_GUEST_BADGE_MAX_CHARS)}…`
                    : guestFirstRaw
                : '';

    const useGuest = mode === BADGE_DISPLAY_GUEST && guestDisplayLine;
    const fullText = useGuest ? guestDisplayLine : confirmationNumber || '—';
    const display =
        useGuest && fullText.length > MONITOR_GUEST_BADGE_MAX_CHARS
            ? `${fullText.slice(0, MONITOR_GUEST_BADGE_MAX_CHARS - 1)}…`
            : fullText.length > MONITOR_BADGE_MAX_CHARS && !useGuest
              ? `${fullText.slice(0, MONITOR_BADGE_MAX_CHARS - 1)}…`
              : fullText;

    const clickKey = confirmationNumber || guestLast || guestFirstRaw;
    const fullGuestTooltip =
        guestFirstRaw && guestLast
            ? `${guestFirstRaw} ${guestLast}`
            : guestLast || guestFirstRaw || '';
    const tooltip = useGuest
        ? `Click to view reservation${confirmationNumber ? ` ${confirmationNumber}` : ''}${fullGuestTooltip ? ` (${fullGuestTooltip})` : ''}`
        : `Click to view reservation profile for ${confirmationNumber}`;

    return {
        display,
        tooltip,
        confirmationForClick: clickKey
    };
}
